import { useQueryClient } from "@tanstack/react-query";
import { notificacion, notificacionError } from "@/components/Alerts";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "@/components/modals/BaseModal";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import type { Respuesta } from "@/ts/typings/clases";
import { keysColumn, useEstanteria } from "../GestionUbicaciones";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Loading } from "@/components/Loading/Loading";

interface ModalEditarFilaProps extends BaseModalProps {
	readonly currentZona: string;
	readonly currentEstanteria: string;
}
export function ModalAddUbicaciones(props: ModalEditarFilaProps) {
	const { close } = props;
	const queryClient = useQueryClient();
	const [config, setConfig] = useState({
		zona: props.currentZona,
		estanteria: props.currentEstanteria,
		columnas: "A-A",
		alturas: "01-01"
	});
	const [excluded, setExcluded] = useState<string[]>([]);

	const ubicacionesExistentes = useEstanteria(config.zona, config.estanteria);

	useEffect(() => {
		const existingLocations = ubicacionesExistentes.data ?? [];

		const columnas = existingLocations
			.map(ubi => keysColumn.indexOf(ubi.idColumna))
			.filter(index => index !== -1);

		const alturas = existingLocations
			.map(ubi => Number(ubi.idAltura))
			.filter(altura => Number.isInteger(altura) && altura >= 1 && altura <= 99);

		const minColIndex = columnas.length ? Math.min(...columnas) : 0;
		const maxColIndex = columnas.length ? Math.max(...columnas) : 0;
		const minAlt = alturas.length ? Math.min(...alturas) : 1;
		const maxAlt = alturas.length ? Math.max(...alturas) : 1;

		const minCol = keysColumn[minColIndex];
		const maxCol = keysColumn[maxColIndex];

		setConfig(old => ({
			...old,
			columnas: minCol === "L" ? "A-A" : `${minCol}-${maxCol}`,
			alturas: minAlt === 99 ? "01-01" : `${minAlt}-${maxAlt}`,
		}));

		const existingIds = new Set(existingLocations.map(ubi => ubi.idUbicacion));
		const defaultExcluded: string[] = [];

		if (existingLocations.length > 0) {
			for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
				for (let rowIndex = minAlt; rowIndex <= maxAlt; rowIndex++) {
					const idUbicacion = `${config.zona}${config.estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;

					if (!existingIds.has(idUbicacion)) {
						defaultExcluded.push(idUbicacion);
					}
				}
			}
		}

		setExcluded(defaultExcluded);
	}, [ubicacionesExistentes.data, config.zona, config.estanteria]);

	useEffect(() => {
		setConfig(old => ({
			...old,
			zona: props.currentZona,
			estanteria: props.currentEstanteria,
		}));
	}, [props.currentEstanteria, props.currentZona]);

	const ubicacionesCalientes = useMemo(() => {
		if (ubicacionesExistentes.data == null) return null;
		return getUbicacionesVisualizar(config, excluded, ubicacionesExistentes.data);
	}, [config, excluded, ubicacionesExistentes.data]);

	const guardarUbicacion = useCallback((event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (ubicacionesCalientes == null) {
			notificacionError("Configuración incorrecta. Comprueba la zona, fila, columnas y alturas.");
			return;
		}

		const ubicaciones = ubicacionesCalientes
			.filter(ubi => ubi.nueva === true && ubi.excluded === false)
			.map(ubi => ({
				idUbicacion: ubi.idUbicacion,
				idAlmacen: ubi.idAlmacen,
				idEstanteria: ubi.idEstanteria,
				idColumna: ubi.idColumna,
				idAltura: ubi.idAltura,
			}));

		if (ubicaciones.length === 0) {
			notificacionError("Selecciona al menos una ubicación para guardar.");
			return;
		}

		void sendAjaxRequest<Respuesta>("POST", "/api/v1/insertUbicaciones", { ubicaciones })
			.then(response => {
				if (response.tipo === "error") {
					notificacionError(response.message ?? "Error al guardar la ubicación.");
					return;
				}

				notificacion(response.message ?? "Ubicación guardada con éxito.");
				close();
				void queryClient.invalidateQueries({ queryKey: ["getEstanterias"] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", config.zona, config.estanteria] });
			})
			.catch(() => {
				notificacionError("Error al guardar la ubicación.");
			});
	}, [close, queryClient, config, ubicacionesCalientes]);

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Ubicación</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<form onSubmit={guardarUbicacion}>
				<div className="modal-body">
					<div style={{ display: "flex", justifyContent: "space-between", gap: "15px", width: "85vw", height: "calc(85vh - 160px)", overflow: "hidden" }}>
						<FormNuevaUbicacion config={config} setConfig={setConfig} ubicacionesExistentes={ubicacionesExistentes.data ?? []} />
						{ubicacionesCalientes != null && <Estanteria zona={config.zona} fila={config.estanteria} ubicaciones={ubicacionesCalientes} setExcluded={setExcluded} />}
						{ubicacionesCalientes == null && <div style={{ width: "100%", height: "100%" }}>
							{ubicacionesExistentes.isPending ? <Loading /> : <p>Configuración incorrecta. Las filas deben de estar en el rango 01-99 y las columnas en el rango A-L.</p>}
						</div>}
					</div>
				</div>
				<div className="modal-footer">
					<button type="submit" className="btnPrimary">Guardar</button>
					<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				</div>
			</form>
		</BaseModal>
	);
}

interface ConfigUbicaciones {
	zona: string;
	estanteria: string;
	columnas: string;
	alturas: string;
}
function FormNuevaUbicacion(props: { readonly config: ConfigUbicaciones; readonly setConfig: Setter<ConfigUbicaciones>; readonly ubicacionesExistentes: BasicCeldaInfo[]; }) {
	const { config, setConfig, ubicacionesExistentes } = props;

	const columnasExistentes = ubicacionesExistentes
		.map(ubi => keysColumn.indexOf(ubi.idColumna))
		.filter(index => index !== -1);

	const alturasExistentes = ubicacionesExistentes
		.map(ubi => Number(ubi.idAltura))
		.filter(altura => Number.isInteger(altura) && altura >= 1 && altura <= 99);

	const minColExistente = columnasExistentes.length > 0 ? Math.min(...columnasExistentes) : null;
	const maxColExistente = columnasExistentes.length > 0 ? Math.max(...columnasExistentes) : null;
	const minAltExistente = alturasExistentes.length > 0 ? Math.min(...alturasExistentes) : null;
	const maxAltExistente = alturasExistentes.length > 0 ? Math.max(...alturasExistentes) : null;

	return (
		<div id="divIdUbicacion" >
			<div>
				<label className="labelsForm">Zona</label>
				<select className="inputsForm" name="zona" value={config.zona} onChange={event => {
					setConfig(old => ({ ...old, zona: event.target.value }));
				}} required>
					<option value="A">A</option>
					<option value="B">B</option>
					<option value="C">C</option>
					<option value="D">D</option>
					<option value="E">E</option>
					<option value="F">F</option>
					<option value="G">G</option>
					<option value="H">H</option>
					<option value="I">I</option>
					<option value="J">J</option>
					<option value="K">K</option>
					<option value="L">L</option>
				</select>
			</div>
			<div>
				<label className="labelsForm">Fila</label>
				<input type="number" className="inputsForm" name="fila" value={config.estanteria} onChange={event => {
					const number = Number(event.target.value.replace(/\D/g, ""));
					setConfig(old => ({
						...old,
						estanteria: number == 0 ? "0" : number.toString().slice(0, 2).padStart(2, "0")
					}));
				}} min={1} max={99} step={1} required />
			</div>
			<div>
				<label className="labelsForm">Columna</label>
				<input type="text" className="inputsForm" name="columna" placeholder="A-#" value={config.columnas} onChange={event => {
					setConfig(old => {
						const value = event.target.value.toUpperCase().replace(/[^A-L]/g, "").slice(0, 2);
						let columnas = value.length === 2 ? `${value[0]}-${value[1]}` : value;

						if (value.length === 2 && minColExistente != null && maxColExistente != null) {
							const inicio = Math.min(keysColumn.indexOf(value[0] as typeof keysColumn[number]), minColExistente);
							const fin = Math.max(keysColumn.indexOf(value[1] as typeof keysColumn[number]), maxColExistente);

							columnas = `${keysColumn[inicio]}-${keysColumn[fin]}`;
						}

						return { ...old, columnas };
					});
				}} required />
			</div>
			<div>
				<label className="labelsForm">Altura</label>
				<input type="text" className="inputsForm" name="altura" placeholder="1-#" value={config.alturas} onChange={event => {
					setConfig(old => {
						const [inicio, ...resto] = event.target.value.replace(/[^\d-]/g, "").split("-");
						const inicioValue = inicio.slice(0, 2);
						const finValue = resto.join("").slice(0, 2);

						let alturas = resto.length > 0
							? `${inicioValue}-${finValue}`
							: inicioValue;

						if (
							resto.length > 0 &&
							/^\d{1,2}$/u.test(inicioValue) &&
							/^\d{1,2}$/u.test(finValue) &&
							minAltExistente != null &&
							maxAltExistente != null
						) {
							const inicioAltura = Math.min(Number(inicioValue), minAltExistente);
							const finAltura = Math.max(Number(finValue), maxAltExistente);

							alturas = `${String(inicioAltura).padStart(2, "0")}-${String(finAltura).padStart(2, "0")}`;
						}

						return { ...old, alturas };
					});
				}} maxLength={5} required />
			</div>
		</div>
	);
}

function Estanteria(props: { readonly zona: string, readonly fila: string; readonly ubicaciones: BasicCeldaInfo[]; readonly setExcluded: Setter<string[]>; }) {
	const onClickUbi = (idUbi: BasicCeldaInfo) => {
		if (idUbi.nueva !== true) return;

		props.setExcluded(old => old.includes(idUbi.idUbicacion)
			? old.filter(oldUbi => oldUbi !== idUbi.idUbicacion)
			: [...old, idUbi.idUbicacion]
		);
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", padding: "20px", border: "1px solid #ccc", justifyContent: "center", alignItems: "center", height: "100%", width: "100%", overflow: "hidden" }}>
			<h1 style={{ textAlign: "center", marginTop: "auto", position: "sticky" }}>{props.zona}{props.fila}</h1>
			<div className="tablaEstanteria">
				{props.ubicaciones.map(ubicacion => (
					<CeldaEstanteria key={ubicacion.idUbicacion} ubicacion={ubicacion} onClick={() => { onClickUbi(ubicacion); }} />
				))}
			</div>
		</div>
	);
}
function CeldaEstanteria(props: { readonly ubicacion: BasicCeldaInfo, readonly onClick?: () => void; }) {
	const claseFondo = props.ubicacion.nueva === true
		? props.ubicacion.excluded ? "fondoGone hoverBright" : "fondoVerde hoverDark"
		: "";

	const idColumna = keysColumn.indexOf(props.ubicacion.idColumna) + 1;
	const idAltura = Number(props.ubicacion.idAltura) * -1;
	const style = {
		gridColumnStart: String(idColumna),
		gridColumnEnd: String(idColumna),
		gridRowStart: String(idAltura),
		gridRowEnd: String(idAltura),
		cursor: props.ubicacion.nueva === true ? "pointer" : "default"
	};
	return (
		<div className={`celdaUbicacion ${claseFondo}`} style={style} onClick={props.onClick}>
			<h3>{props.ubicacion.idUbicacion}</h3>
		</div>
	);
}


function getUbicacionesVisualizar(config: ConfigUbicaciones, excluded: string[], ubicacionesExistentes: BasicCeldaInfo[]): BasicCeldaInfo[] | null {
	const { zona, estanteria, columnas, alturas } = config;

	if (!/^[A-L]$/u.test(zona) || !/^\d{1,2}$/u.test(estanteria)) return null;

	const fila = Number(estanteria);
	if (fila < 1 || fila > 99) return null;

	const columnasMatch = /^([A-L])-([A-L])$/u.exec(columnas);
	const alturasMatch = /^(\d{1,2})-(\d{1,2})$/u.exec(alturas);

	if (!columnasMatch || !alturasMatch) return null;

	const startColIndex = keysColumn.indexOf(columnasMatch[1] as typeof keysColumn[number]);
	const endColIndex = keysColumn.indexOf(columnasMatch[2] as typeof keysColumn[number]);
	const startRowIndex = Number(alturasMatch[1]);
	const endRowIndex = Number(alturasMatch[2]);

	if (
		startColIndex === -1 || endColIndex === -1 || startColIndex > endColIndex ||
		startRowIndex < 1 || endRowIndex > 99 || startRowIndex > endRowIndex
	) return null;

	const lista: BasicCeldaInfo[] = [...ubicacionesExistentes];
	const ubicacionesPorId = new Map(lista.map(ubi => [ubi.idUbicacion, ubi]));
	const excludedIds = new Set(excluded);

	const agregarUbicacion = (colIndex: number, rowIndex: number, excluded: boolean) => {
		const idColumna = keysColumn[colIndex];
		const idAltura = String(rowIndex).padStart(2, "0");
		const idUbicacion = `${zona}${estanteria}${idColumna}${idAltura}`;
		const existente = ubicacionesPorId.get(idUbicacion);

		if (existente != null) return;

		const nueva: BasicCeldaInfo = {
			idUbicacion,
			idAlmacen: zona,
			idEstanteria: estanteria,
			idColumna,
			idAltura,
			nueva: true,
			excluded,
		};

		lista.push(nueva);
		ubicacionesPorId.set(idUbicacion, nueva);
	};

	for (let colIndex = startColIndex; colIndex <= endColIndex; colIndex++) {
		for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
			const idUbicacion = `${zona}${estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;

			if (!ubicacionesPorId.has(idUbicacion)) {
				agregarUbicacion(colIndex, rowIndex, excludedIds.has(idUbicacion));
			}
		}
	}

	const columnasExistentes = ubicacionesExistentes
		.map(ubi => keysColumn.indexOf(ubi.idColumna))
		.filter(index => index !== -1);

	const alturasExistentes = ubicacionesExistentes
		.map(ubi => Number(ubi.idAltura))
		.filter(altura => Number.isInteger(altura) && altura >= 1 && altura <= 99);

	if (columnasExistentes.length > 0 && alturasExistentes.length > 0) {
		const minColExistente = Math.min(...columnasExistentes);
		const maxColExistente = Math.max(...columnasExistentes);
		const minAltExistente = Math.min(...alturasExistentes);
		const maxAltExistente = Math.max(...alturasExistentes);

		for (let colIndex = minColExistente; colIndex <= maxColExistente; colIndex++) {
			for (let rowIndex = minAltExistente; rowIndex <= maxAltExistente; rowIndex++) {
				agregarUbicacion(colIndex, rowIndex, true);
			}
		}
	}

	return lista;
}

export type BasicCeldaInfo = {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	articulos: Array<{
		cantidad: number;
		esFijo: boolean;
		idArticulo: string;
		idFase: number;
	}>;

	nueva?: false;
} | {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	nueva: true;
	excluded: boolean;
};